'use client'

import { useCallback, useEffect, useState } from 'react'
import { Link } from '@/shared/navigation'
import { confirmReturnPickup } from '@/features/delman/api/confirmReturnPickup'
import { delmanTerms } from '@/features/delman/delmanTerms'
import {
  createEmptyTodayTrip,
  getOrCreateTodayTrip,
  saveTripRecord,
  type TripRecord,
} from '@/features/delman/storage/tripStorage'
import { loadTodaySessions } from '@/features/delman/api/delmanTripApi'
import { shouldUseDelmanMock } from '@/features/delman/api/delmanApiMode'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { EmptyState } from '@/shared/components/EmptyState'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QrCameraScanner } from '@/shared/components/QrCameraScanner'
import { SkeletonList } from '@/shared/components/Skeleton'
import { Toast } from '@/shared/components/Toast'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'
import { parseReturnPickupQrPayload } from '@/shared/utils/qrHandoverPayload'

export function DelmanPickupReturnPage() {
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null
  const delmanId = device?.delmanId
  const delmanName = device?.delmanName ?? 'Delman'
  const kitchenName = device?.kitchenName ?? 'Dapur'

  const [trip, setTrip] = useState<TripRecord>(() =>
    shouldUseDelmanMock()
      ? getOrCreateTodayTrip(delmanName, kitchenName)
      : createEmptyTodayTrip(delmanName, kitchenName),
  )
  const [isLoadingTrip, setIsLoadingTrip] = useState(false)
  const [tripLoaded, setTripLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [scanInput, setScanInput] = useState('')
  const [selectedReturnId, setSelectedReturnId] = useState<string | null>(null)
  const [pinOpen, setPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [cameraActive, setCameraActive] = useState(true)
  const [manualOpen, setManualOpen] = useState(false)

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const loadTrip = useCallback(async () => {
    const currentDevice = getDelmanDevice()
    if (!currentDevice) return

    setIsLoadingTrip(true)
    setLoadError(null)
    try {
      const sessions = await loadTodaySessions({
        delmanId: currentDevice.delmanId,
        delmanName: currentDevice.delmanName,
        kitchenName: currentDevice.kitchenName,
      })
      setTrip(sessions.active)
      setLoadError(sessions.apiError ? delmanTerms.returnPickup.loadError : null)
    } catch {
      setLoadError(delmanTerms.returnPickup.loadError)
      setTrip(createEmptyTodayTrip(currentDevice.delmanName, currentDevice.kitchenName))
    } finally {
      setIsLoadingTrip(false)
      setTripLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (!hydrated || !delmanId) return
    void loadTrip()
  }, [hydrated, delmanId, loadTrip, refreshKey])

  const tripStarted = trip.status === 'in_progress' || trip.status === 'completed'
  const pending = trip.returnPickups.filter((r) => r.status === 'waiting_scan')
  const picked = trip.returnPickups.filter((r) => r.status === 'picked_up')

  function findPendingMatch(payload: string) {
    const parsed = parseReturnPickupQrPayload(payload)
    if (!parsed) return undefined
    const inList = pending.find((r) => r.id.toLowerCase() === parsed.returnId.toLowerCase())
    if (inList) return inList
    return {
      id: parsed.returnId,
      returnCode: parsed.returnId.slice(0, 8).toUpperCase(),
      storeName: 'Toko',
      itemCount: 0,
      destination: 'to_kitchen' as const,
      status: 'waiting_scan' as const,
      lines: [],
    }
  }

  function beginPickup(matchId: string, payload: string) {
    setScanInput(payload.trim())
    setSelectedReturnId(matchId)
    setCameraActive(false)
    setPinOpen(true)
  }

  function handleScanSubmit() {
    const payload = scanInput.trim()
    if (!payload) {
      showToast(delmanTerms.returnPickup.emptyScan)
      return
    }

    const match = findPendingMatch(payload)
    if (!match) {
      showToast(delmanTerms.returnPickup.unknownQr)
      return
    }

    beginPickup(match.id, payload)
  }

  const handleCameraScan = useCallback(
    (value: string) => {
      if (!tripStarted) return
      const match = findPendingMatch(value)
      if (!match) {
        showToast(delmanTerms.returnPickup.unknownQr)
        return
      }
      showToast(delmanTerms.returnPickup.scanned(match.id))
      beginPickup(match.id, value)
    },
    [tripStarted, pending, showToast],
  )

  function selectPending(docId: string) {
    if (!tripStarted) {
      showToast(delmanTerms.returnPickup.tripNotStarted)
      return
    }
    const doc = pending.find((r) => r.id === docId)
    showToast(
      doc ? delmanTerms.returnPickup.selectPendingHint(doc.returnCode) : delmanTerms.returnPickup.emptyScan,
    )
  }

  async function confirmPickup(pin: string) {
    const currentDevice = getDelmanDevice()
    if (!currentDevice || !selectedReturnId) {
      throw new Error(delmanTerms.returnPickup.incompleteData)
    }

    await confirmReturnPickup({
      returnId: selectedReturnId,
      deviceId: currentDevice.deviceId,
      pin,
      qrPayload: scanInput.trim(),
    })

    const queued = consumeLastSyncQueued() ? ` ${delmanTerms.sync.queued}` : ''
    const pickedAt = new Intl.DateTimeFormat('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date())

    const nextTrip = {
      ...trip,
      returnPickups: trip.returnPickups.map((r) =>
        r.id === selectedReturnId ? { ...r, status: 'picked_up' as const, pickedUpAt: pickedAt } : r,
      ),
    }
    saveTripRecord(nextTrip)
    setTrip(nextTrip)
    setPinOpen(false)
    setSelectedReturnId(null)
    setScanInput('')
    setCameraActive(true)
    showToast(`${delmanTerms.returnPickup.successToast}${queued}`)
  }

  const selectedReturn = trip.returnPickups.find((r) => r.id === selectedReturnId)

  if (!hydrated) {
    return <div className="min-h-[200px]" aria-hidden />
  }

  return (
    <section className="space-y-4">
      <div className="card relative space-y-2">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={isLoadingTrip}
          aria-label={delmanTerms.returnPickup.refreshAria}
          title={delmanTerms.returnPickup.refreshAria}
        >
          <span className={`material-symbols-outlined ${isLoadingTrip ? 'animate-spin' : ''}`}>refresh</span>
        </button>
        <div className="pr-12">
          <p className="text-headline-md text-primary break-words">{delmanTerms.returnPickup.pageTitle}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">{delmanTerms.returnPickup.intro}</p>
        </div>
        <p className="text-body-sm text-on-surface-variant bg-surface-container rounded-lg px-3 py-2 border border-outline-variant">
          {delmanTerms.returnPickup.whatIsPickup}
        </p>
      </div>

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {delmanTerms.returnPickup.retry}
          </button>
        </div>
      )}

      {isLoadingTrip && !tripLoaded && (
        <SkeletonList count={2} label={delmanTerms.returnPickup.loading} />
      )}

      {tripLoaded && !tripStarted && (
        <div className="card flex items-start gap-2 border-status-warning/40 bg-status-warning-bg/30">
          <span className="material-symbols-outlined text-status-warning shrink-0">info</span>
          <p className="text-body-sm text-status-warning">{delmanTerms.returnPickup.tripNotStarted}</p>
        </div>
      )}

      {(tripLoaded || !isLoadingTrip) && (
        <>
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-body-sm font-semibold">{delmanTerms.returnPickup.scan}</p>
              {tripStarted && cameraActive && !pinOpen && (
                <span className="badge-warning text-body-sm">{delmanTerms.returnPickup.liveBadge}</span>
              )}
            </div>
            <QrCameraScanner
              active={tripStarted && cameraActive && !pinOpen}
              inactiveMessage={
                !tripStarted
                  ? delmanTerms.returnPickup.cameraInactiveTrip
                  : pinOpen
                    ? delmanTerms.returnPickup.cameraInactivePin
                    : delmanTerms.returnPickup.cameraHint
              }
              onScan={handleCameraScan}
              unsupportedMessage={delmanTerms.returnPickup.cameraUnsupported}
              permissionDeniedMessage={delmanTerms.returnPickup.cameraPermissionDenied}
              scanningHint={delmanTerms.returnPickup.cameraHint}
            />

            <details
              className="rounded-lg border border-outline-variant/60 bg-surface-container-low"
              open={manualOpen}
              onToggle={(e) => setManualOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer select-none px-3 py-3 text-body-sm font-medium min-h-[44px] flex items-center gap-2">
                <span className="material-symbols-outlined text-lg text-on-surface-variant">keyboard</span>
                {delmanTerms.returnPickup.manualToggle}
              </summary>
              <div className="space-y-3 border-t border-outline-variant/40 px-3 pb-3 pt-2">
                <label className="field-label" htmlFor="return_qr_payload">
                  {delmanTerms.returnPickup.pasteLabel}
                </label>
                <input
                  id="return_qr_payload"
                  className="input-field font-mono"
                  placeholder={delmanTerms.returnPickup.pastePlaceholder}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={!tripStarted}
                />
                <button
                  type="button"
                  className="btn-primary w-full min-h-[48px] btn-tactile disabled:opacity-40"
                  disabled={!tripStarted || !scanInput.trim()}
                  onClick={handleScanSubmit}
                >
                  {delmanTerms.returnPickup.confirm}
                </button>
              </div>
            </details>
          </div>

          {pending.length > 0 && (
            <div className="card space-y-2">
              <p className="text-body-sm font-semibold text-status-warning">
                {delmanTerms.returnPickup.pendingTitle(pending.length)}
              </p>
              {pending.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => selectPending(doc.id)}
                  disabled={!tripStarted}
                  className="w-full text-left rounded-lg border border-status-warning/30 bg-status-warning-bg/20 p-3 text-body-sm min-h-[44px] hover:border-status-warning/50 transition-colors disabled:opacity-50"
                >
                  <p className="font-mono text-body-sm">{doc.returnCode}</p>
                  <p className="mt-1 break-words">
                    {delmanTerms.returnPickup.pendingItemLine(doc.storeName, doc.itemCount)}
                  </p>
                  {doc.lines.length > 0 && (
                    <ul className="mt-2 space-y-1 border-t border-status-warning/20 pt-2">
                      {doc.lines.map((line, idx) => (
                        <li
                          key={`${line.productName}-${line.productionDate}-${idx}`}
                          className="flex items-center justify-between gap-2 text-body-sm"
                        >
                          <span className="text-on-surface break-words">{line.productName}</span>
                          <span className="font-mono text-on-surface-variant shrink-0">
                            {line.qty} pcs · {line.productionDate}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>
          )}

          {tripLoaded && pending.length === 0 && picked.length === 0 && tripStarted && (
            <EmptyState
              icon="inventory_2"
              title={delmanTerms.returnPickup.emptyPending}
              description={delmanTerms.returnPickup.intro}
            />
          )}

          {picked.length > 0 && (
            <div className="card space-y-2">
              <p className="text-body-sm font-semibold text-status-success">{delmanTerms.returnPickup.pickedTitle}</p>
              {picked.map((doc) => (
                <div key={doc.id} className="rounded-lg bg-surface-container p-3 text-body-sm opacity-80">
                  <p className="font-mono text-body-sm">{doc.returnCode}</p>
                  <p className="mt-1 break-words">
                    {delmanTerms.returnPickup.pickedItemLine(doc.storeName, doc.pickedUpAt ?? '—')}
                  </p>
                  {doc.lines.length > 0 && (
                    <ul className="mt-2 space-y-1 border-t border-outline-variant/40 pt-2">
                      {doc.lines.map((line, idx) => (
                        <li
                          key={`${line.productName}-${line.productionDate}-${idx}`}
                          className="flex items-center justify-between gap-2 text-body-sm"
                        >
                          <span className="break-words">{line.productName}</span>
                          <span className="font-mono shrink-0">
                            {line.qty} pcs · {line.productionDate}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          <Link
            href="/delman/trip"
            className="btn-outline block text-center min-h-[44px] leading-[44px]"
          >
            {delmanTerms.returnPickup.backToTrip}
          </Link>
        </>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={delmanTerms.returnPickup.pinTitle}
        description={
          selectedReturn
            ? delmanTerms.returnPickup.pinDescription(
                selectedReturn.returnCode,
                selectedReturn.itemCount,
                selectedReturn.storeName,
              )
            : delmanTerms.returnPickup.pinFallback
        }
        confirmLabel={delmanTerms.returnPickup.confirm}
        onClose={() => {
          setPinOpen(false)
          setSelectedReturnId(null)
          setCameraActive(true)
        }}
        onConfirm={confirmPickup}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
